import React, { useState } from "react";
import { API, Auth } from "aws-amplify";
import Header from "../../components/header/Header";
import { useNavigate } from "react-router-dom";
import { RiseLoader } from "react-spinners";
import profile from "../../assets/profile.png";
import { buildResumeURL } from "../../utils/resumeUrl";
import { 
  FiCalendar, 
  FiClock, 
  FiMail,
  FiUser,
  FiPhone,
  FiBriefcase,
  FiFileText,
  FiX,
  FiExternalLink,
  FiSearch,
  FiXCircle
} from "react-icons/fi";

const CandidateInterviewSlotList = () => {
  const [dateQuery, setDateQuery] = useState("");
  const [interviewSlots, setInterviewSlots] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const override = {
    display: "block",
    margin: "0 auto",
    borderColor: "#3b82f6",
  };

  const handleDateInputChange = (event) => {
    setDateQuery(event.target.value);
  };

  const handleSearch = async () => {
    if (!dateQuery) {
      setError("Please select a date");
      return;
    }

    try {
      // Check authentication first
      await Auth.currentAuthenticatedUser();
      
      setLoading(true);
      setError(null);
      let nextToken = null;
      let slots = [];

      do {
        let searchInformation;
        try {
          searchInformation = await API.graphql({
            query: `
              query ListInterviewSlots($filter: ModelInterviewSlotFilterInput, $limit: Int, $nextToken: String) {
                listInterviewSlots(filter: $filter, limit: $limit, nextToken: $nextToken) {
                  items {
                    id
                    date
                    startTime
                    endTime
                    candidateEmail
                    meetingLinkURL
                    status
                    createdAt
                  }
                  nextToken
                }
              }
            `,
            variables: {
              filter: { date: { eq: dateQuery } },
              limit: 100,
              nextToken: nextToken,
            }
          });
        } catch (partialError) {
          if (partialError.data && partialError.data.listInterviewSlots) {
            searchInformation = partialError;
          } else {
            throw partialError;
          }
        }

        if (searchInformation.data && searchInformation.data.listInterviewSlots) {
          const { items, nextToken: newToken } = searchInformation.data.listInterviewSlots;
          const validItems = (items || []).filter(item => item && item.candidateEmail);
          slots = [...slots, ...validItems];
          nextToken = newToken;
        } else {
          break;
        }
      } while (nextToken);

      slots.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
      setInterviewSlots(slots);
      
      if (slots.length === 0) {
        setError("No interviews scheduled for this date");
      }
    } catch (error) {
      console.error("Error fetching interview slots:", error);
      if (error === "The user is not authenticated") {
        setError("Please sign in to access admin features.");
      } else if (error.errors && error.errors.length > 0) {
        setError(`${error.errors[0].message}. Please check your permissions.`);
      } else {
        setError("Failed to fetch interview slots. Please check your authentication and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewCandidateInfo = async (email) => {
    try {
      setLoading(true);
      
      let candidateInfoResponse;
      try {
        candidateInfoResponse = await API.graphql({
          query: `
            query ListCandidateProfiles($filter: ModelCandidateProfileFilterInput, $limit: Int) {
              listCandidateProfiles(filter: $filter, limit: $limit) {
                items {
                  id
                  fullName
                  email
                  mobileNumber
                  photoURL
                  roleApplyFor
                  workStatus
                  description
                  keySkills
                  resumeURL
                }
              }
            }
          `,
          variables: {
            filter: { email: { eq: email } },
            limit: 10,
          }
        });
      } catch (partialError) {
        if (partialError.data && partialError.data.listCandidateProfiles) {
          candidateInfoResponse = partialError;
        } else {
          throw partialError;
        }
      }
      
      if (candidateInfoResponse.data && candidateInfoResponse.data.listCandidateProfiles) {
        const items = candidateInfoResponse.data.listCandidateProfiles.items || [];
        const validItems = items.filter(item => item && item.email);
        setSelectedCandidate(validItems.length > 0 ? validItems : null);
        
        if (validItems.length === 0) {
          setError("Candidate profile not found");
        }
      }
    } catch (error) {
      console.error("Error fetching candidate info:", error);
      setError("Failed to fetch candidate information");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedCandidate(null);
  };

  const getEmail = (
    email,
    fullName,
    photoURL,
    workStatus,
    description,
    keySkills
  ) => {
    const url = `/AdminCandidateViewProfile`;
    const queryString = new URLSearchParams({
      email,
      fullName,
      photoURL,
      workStatus,
      description,
      keySkills,
    }).toString();

    window.open(`${url}?${queryString}`);
  };

  const convertTo12HourFormat = (timeString) => {
    const [hours, minutes] = timeString.split(":");
    let parsedHours = parseInt(hours, 10);
    const period = parsedHours >= 12 ? "PM" : "AM";
    parsedHours = parsedHours % 12 || 12;
    return `${parsedHours}:${minutes} ${period}`;
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200">
      <Header />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-medium text-white tracking-tight mb-2">
            Interview Slot Management
          </h1>
          <p className="text-slate-400 text-sm">
            Search and manage scheduled interview slots by date
          </p>
          {localStorage.getItem("candidateProfileEmailID") && (
            <p className="text-slate-500 text-xs mt-2">
              Logged in as: {localStorage.getItem("candidateProfileEmailID")}
            </p>
          )}
        </div>

        {/* Search Section */}
        <div className="bg-slate-800/40 rounded-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block text-slate-400 text-sm mb-2">
                <FiCalendar className="inline mr-2" />
                Select Date
              </label>
              <input
                type="date"
                value={dateQuery}
                onChange={handleDateInputChange}
                className="w-full bg-slate-700 text-white border border-slate-600 rounded-lg py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={loading || !dateQuery}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white px-8 py-2.5 rounded-lg transition-all flex items-center gap-2 font-medium"
            >
              <FiSearch size={18} />
              Search Interviews
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <FiXCircle size={20} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium mb-1">Error</p>
                <p className="text-sm">{error}</p>
                {error.includes("permission") || error.includes("authentication") || error.includes("sign in") ? (
                  <p className="text-xs mt-2 text-red-300">
                    Tip: Make sure you're logged in with an admin account
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <RiseLoader color={"#3b82f6"} loading={loading} css={override} size={15} />
          </div>
        )}

        {/* Interview Slots Table */}
        {!loading && interviewSlots.length > 0 && (
          <div className="bg-slate-800/40 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white">
                Found {interviewSlots.length} Interview{interviewSlots.length !== 1 ? 's' : ''} on {new Date(dateQuery).toLocaleDateString()}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50 text-slate-300 text-xs uppercase">
                  <tr>
                    <th className="px-6 py-3 text-left">Date</th>
                    <th className="px-6 py-3 text-left">Candidate Email</th>
                    <th className="px-6 py-3 text-left">Start Time</th>
                    <th className="px-6 py-3 text-left">End Time</th>
                    <th className="px-6 py-3 text-left">Meeting Link</th>
                    <th className="px-6 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {interviewSlots
                    .sort((a, b) => a.startTime.localeCompare(b.startTime))
                    .map((slot) => (
                      <tr
                        key={slot.id}
                        className="hover:bg-slate-700/30 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-slate-300">
                            <FiCalendar className="text-blue-400" size={16} />
                            <span className="text-sm">{slot.date}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-slate-300">
                            <FiMail className="text-blue-400" size={16} />
                            <span className="text-sm">{slot.candidateEmail}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-slate-300">
                            <FiClock className="text-green-400" size={16} />
                            <span className="text-sm">{convertTo12HourFormat(slot.startTime)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-slate-300">
                            <FiClock className="text-red-400" size={16} />
                            <span className="text-sm">{convertTo12HourFormat(slot.endTime)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {slot.meetingLinkURL ? (
                            <a
                              href={slot.meetingLinkURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 hover:underline"
                            >
                              <FiExternalLink size={14} />
                              Join Meeting
                            </a>
                          ) : (
                            <span className="text-slate-500 text-sm">No link</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center space-x-2">
                          <button
                            onClick={() => handleViewCandidateInfo(slot.candidateEmail)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                          >
                            Quick View
                          </button>
                          <button
                            onClick={() => {
                              const params = new URLSearchParams({
                                email: slot.candidateEmail,
                                slotId: slot.id
                              });
                              window.open(`/AdminCandidateViewProfile?${params.toString()}`, '_blank');
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                          >
                            Full Profile
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {!loading && interviewSlots.length === 0 && dateQuery && (
          <div className="bg-slate-800/40 rounded-xl p-12 text-center">
            <FiCalendar className="text-slate-600 text-5xl mx-auto mb-4" />
            <p className="text-slate-400">No interviews scheduled for this date</p>
          </div>
        )}

        {/* Candidate Info Modal */}
        {selectedCandidate && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50 p-4 overflow-y-auto">
            <div className="bg-slate-800 rounded-xl max-w-2xl w-full my-8 border border-slate-700">
              <div className="px-6 py-4 border-b border-slate-700 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <FiUser className="text-blue-400" />
                  Candidate Information
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {selectedCandidate.map((candidate) => (
                  <div key={candidate.id} className="space-y-4">
                    <div className="flex items-center gap-4">
                      <img 
                        src={candidate.photoURL || profile} 
                        alt="Candidate" 
                        className="w-20 h-20 rounded-full object-cover border-4 border-slate-700"
                      />
                      <div>
                        <h3 className="text-xl font-semibold text-white">{candidate.fullName}</h3>
                        <p className="text-slate-400 text-sm">{candidate.roleApplyFor || 'No role specified'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                      <div className="space-y-1">
                        <label className="text-slate-500 text-xs uppercase">Email</label>
                        <div className="flex items-center gap-2 text-slate-300">
                          <FiMail className="text-blue-400" size={14} />
                          <p className="text-sm">{candidate.email}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <label className="text-slate-500 text-xs uppercase">Phone</label>
                        <div className="flex items-center gap-2 text-slate-300">
                          <FiPhone className="text-blue-400" size={14} />
                          <p className="text-sm">{candidate.mobileNumber || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="text-slate-500 text-xs uppercase">Role</label>
                        <div className="flex items-center gap-2 text-slate-300">
                          <FiBriefcase className="text-blue-400" size={14} />
                          <p className="text-sm">{candidate.roleApplyFor || 'N/A'}</p>
                        </div>
                      </div>

                      {buildResumeURL(candidate.resumeURL) && (
                        <div className="space-y-1">
                          <label className="text-slate-500 text-xs uppercase">Resume</label>
                          <a
                            href={buildResumeURL(candidate.resumeURL)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
                          >
                            <FiFileText size={14} />
                            View Resume
                          </a>
                        </div>
                      )}
                    </div>

                    {candidate.description && (
                      <div className="pt-4 border-t border-slate-700">
                        <label className="text-slate-500 text-xs uppercase block mb-2">Description</label>
                        <p className="text-slate-300 text-sm leading-relaxed">{candidate.description}</p>
                      </div>
                    )}

                    {candidate.keySkills && (
                      <div className="pt-4 border-t border-slate-700">
                        <label className="text-slate-500 text-xs uppercase block mb-2">Key Skills</label>
                        <p className="text-slate-300 text-sm">{candidate.keySkills}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="px-6 py-4 border-t border-slate-700 flex gap-3">
                <button
                  onClick={() =>
                    getEmail(
                      selectedCandidate[0].email,
                      selectedCandidate[0].fullName,
                      selectedCandidate[0].photoURL,
                      selectedCandidate[0].workStatus,
                      selectedCandidate[0].description,
                      selectedCandidate[0].keySkills
                    )
                  }
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition-all"
                >
                  View Full Profile
                </button>
                <button
                  onClick={handleCloseModal}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold px-4 py-2 rounded-lg transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateInterviewSlotList;
